'use client';

import React, { useState, useRef, useEffect } from 'react';
import Accent from '@/components/shared/Accent';
import GlowingButton from '@/components/shared/GlowingButton';
import Image from 'next/image';
import { HiScale } from 'react-icons/hi';

import Breadcrumb from '@/components/shared/Breadcrumb';

export default function ContactClient() {
  const breadcrumbItems = [{ label: 'Contact' }];
  const [isLoading, setIsLoading] = useState(false);
  const [statusType, setStatusType] = useState<'success' | 'error' | null>(
    null
  );
  const [statusMessage, setStatusMessage] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setStatusType(null);
    setStatusMessage('');

    try {
      const formData = new FormData(e.currentTarget);
      const payload = {
        name: formData.get('user_name'),
        email: formData.get('user_email'),
        subject: formData.get('subject'),
        message: formData.get('message'),
        website: formData.get('website'), // honeypot
      };

      // API call ke endpoint send-email
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setStatusType('success');
        setStatusMessage(data.message || 'Form submitted successfully!');
        formRef.current?.reset();
      } else {
        setStatusType('error');
        setStatusMessage(
          data.error || 'Failed to submit form. Please try again later.'
        );
      }
    } catch (error) {
      // Handle form submission error
      setStatusType('error');
      setStatusMessage('Failed to submit form. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto ">
      <div className="mb-8" data-fade="2">
        <Breadcrumb items={breadcrumbItems} pagePath="/contact/" />
      </div>

      <div className="mx-auto md:col-span-2 h-full">
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="space-y-6"
          data-fade="5"
        >
          {/* Honeypot: invisible to humans, bots auto-fill this */}
          <div
            style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}
            aria-hidden="true"
          >
            <label htmlFor="website">Website</label>
            <input
              type="text"
              name="website"
              id="website"
              tabIndex={-1}
              autoComplete="off"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label
                htmlFor="user_name"
                className="block text-sm font-medium text-neutral-300"
              >
                Name
              </label>
              <input
                type="text"
                name="user_name"
                id="user_name"
                required
                className="w-full bg-transparent border-b border-dotted border-neutral-700 hover:border-neutral-600 focus:border-neutral-500 transition-colors px-0 py-2 focus:outline-none focus:ring-0 placeholder:text-neutral-600"
                placeholder="Your name"
              />
            </div>
            <div className="space-y-2">
              <label
                htmlFor="user_email"
                className="block text-sm font-medium text-neutral-300"
              >
                Email
              </label>
              <input
                type="email"
                name="user_email"
                id="user_email"
                required
                className="w-full bg-transparent border-b border-dotted border-neutral-700 hover:border-neutral-600 focus:border-neutral-500 transition-colors px-0 py-2 focus:outline-none focus:ring-0 placeholder:text-neutral-600"
                placeholder="your@email.com"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="subject"
              className="block text-sm font-medium text-neutral-300"
            >
              Subject
            </label>
            <input
              type="text"
              name="subject"
              id="subject"
              required
              className="w-full bg-transparent border-b border-dotted border-neutral-700 hover:border-neutral-600 focus:border-neutral-500 transition-colors px-0 py-2 focus:outline-none focus:ring-0 placeholder:text-neutral-600"
              placeholder="Subject"
            />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="message"
              className="block text-sm font-medium text-neutral-300"
            >
              Message
            </label>
            <textarea
              name="message"
              id="message"
              required
              rows={6}
              className="w-full bg-transparent border-b border-dotted border-neutral-700 hover:border-neutral-600 focus:border-neutral-500 transition-colors px-0 py-2 focus:outline-none focus:ring-0 resize-none placeholder:text-neutral-600"
              placeholder="Your message"
            />
          </div>

          {statusMessage && (
            <div
              className={`p-4 rounded-lg ${
                statusType === 'success'
                  ? 'bg-emerald-900/30 text-emerald-400'
                  : 'bg-red-900/30 text-red-400'
              }`}
            >
              {statusMessage}
            </div>
          )}

          <div className="flex items-center justify-end pt-2" data-fade="6">
            <GlowingButton
              type="submit"
              disabled={isLoading}
              isLoading={isLoading}
              variant="small"
              iconPosition="right"
            >
              Send Message
            </GlowingButton>
          </div>
        </form>
      </div>
    </div>
    // </div>
  );
}
